// 产品矩阵系统 - 卡片样式编辑面板
// 可折叠的左侧面板，用于控制卡片内容显示
import React, { useState } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Type, 
  Hash, 
  MessageSquare, 
  List, 
  Palette,
  Settings2,
  Combine,
  Scissors
} from 'lucide-react';
import { useCanvasStore } from '../stores/canvas-store';

interface CardStylePanelProps {
  isDarkMode?: boolean;
}

export function CardStylePanel({ isDarkMode = false }: CardStylePanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const { 
    cardContentSettings, 
    updateCardContentSettings,
    isMergeMode,
    toggleMergeMode,
    selectedCells,
    mergeCells,
    clearCellSelection,
    showCanvasBackground,
    canvasBackgroundColor,
    setCanvasBackgroundColor,
    toggleCanvasBackground,
    unmergeCells,
    canvasData,
  } = useCanvasStore();

  // 检查选中的单元格是否是合并单元格
  const getSelectedMergedCell = () => {
    if (selectedCells.length !== 1) return null;
    const cellIndex = selectedCells[0];
    const cell = canvasData.cells[cellIndex];
    if (cell && (cell.rowSpan && cell.rowSpan > 1) || (cell?.colSpan && cell.colSpan > 1)) {
      return cellIndex;
    }
    return null;
  };
  
  const selectedMergedCellIndex = getSelectedMergedCell();

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  // 文字颜色预设
  const colorPresets = [
    { name: '默认', value: '' },
    { name: '绿色', value: '#22c55e' },
    { name: '蓝色', value: '#3b82f6' },
    { name: '紫色', value: '#a855f7' },
    { name: '橙色', value: '#f97316' },
    { name: '红色', value: '#ef4444' },
    { name: '白色', value: '#ffffff' },
    { name: '灰色', value: '#9ca3af' },
  ];

  // 背景颜色预设（参考图片设计）
  const bgColorPresets = [
    { name: '透明', value: 'transparent', icon: 'checkerboard' },
    { name: '白色', value: '#ffffff', icon: 'solid' },
    { name: '纯黑', value: '#000000', icon: 'solid' },
    { name: '深灰', value: '#1f2937', icon: 'solid' },
  ];

  // 更多背景颜色选项
  const moreBgColors = [
    '#000000', '#1f2937', '#374151', '#4b5563', '#6b7280', '#9ca3af',
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
    '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
    '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#ffffff',
  ];

  if (isCollapsed) {
    return (
      <div className={`w-10 flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r flex flex-col items-center py-4`}>
        <button
          onClick={toggleCollapse}
          className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}
          title="展开样式面板"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <div className="mt-4 flex flex-col gap-2">
          <div className={`p-1.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`} title="样式设置">
            <Settings2 className="w-4 h-4 text-blue-500" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-64 flex-shrink-0 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r flex flex-col overflow-hidden`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-blue-500" />
          <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            卡片样式
          </span>
        </div>
        <button
          onClick={toggleCollapse}
          className={`p-1.5 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* 单元格合并 */}
        <div>
          <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            单元格合并
          </h4>
          <div className="space-y-2">
            <button
              onClick={toggleMergeMode}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                isMergeMode
                  ? 'bg-blue-500 text-white'
                  : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Combine className="w-4 h-4" />
              {isMergeMode ? '退出合并模式' : '进入合并模式'}
            </button>
            
            {isMergeMode && (
              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>
                  点击空白单元格进行多选，然后点击"合并"按钮
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={mergeCells}
                    disabled={selectedCells.length < 2}
                    className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      selectedCells.length >= 2
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : isDarkMode
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    合并 ({selectedCells.length})
                  </button>
                  <button
                    onClick={clearCellSelection}
                    className={`px-3 py-1.5 rounded text-xs font-medium ${
                      isDarkMode
                        ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    清除
                  </button>
                </div>
                
                {/* 拆分按钮 - 当选中单个合并单元格时显示 */}
                {selectedMergedCellIndex !== null && (
                  <button
                    onClick={() => {
                      unmergeCells(selectedMergedCellIndex);
                      clearCellSelection();
                    }}
                    className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs font-medium bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                  >
                    <Scissors className="w-3 h-3" />
                    拆分选中的合并单元格
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 显示内容控制 */}
        <div>
          <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            显示内容
          </h4>
          <div className="space-y-2">
            {/* 标题 */}
            <ToggleItem
              icon={<Type className="w-4 h-4" />}
              label="产品标题"
              checked={cardContentSettings.showTitle}
              onChange={(v) => updateCardContentSettings({ showTitle: v })}
              isDarkMode={isDarkMode}
            />
            {/* 型号范围 */}
            <ToggleItem
              icon={<Hash className="w-4 h-4" />}
              label="型号/间距"
              checked={cardContentSettings.showModelRange}
              onChange={(v) => updateCardContentSettings({ showModelRange: v })}
              isDarkMode={isDarkMode}
            />
            {/* Slogan */}
            <ToggleItem
              icon={<MessageSquare className="w-4 h-4" />}
              label="Slogan/场景"
              checked={cardContentSettings.showSlogan}
              onChange={(v) => updateCardContentSettings({ showSlogan: v })}
              isDarkMode={isDarkMode}
            />
            {/* 特点列表 */}
            <ToggleItem
              icon={<List className="w-4 h-4" />}
              label="特点列表"
              checked={cardContentSettings.showFeatures}
              onChange={(v) => updateCardContentSettings({ showFeatures: v })}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>

        {/* 特点数量 */}
        {cardContentSettings.showFeatures && (
          <div>
            <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              特点数量
            </h4>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((num) => (
                <button
                  key={num}
                  onClick={() => updateCardContentSettings({ featuresCount: num })}
                  className={`flex-1 py-1.5 text-sm font-medium rounded transition-colors ${
                    cardContentSettings.featuresCount === num
                      ? 'bg-blue-500 text-white'
                      : isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 颜色设置 */}
        <div>
          <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            颜色设置
          </h4>
          <div className="space-y-3">
            {/* Slogan 颜色 */}
            <ColorPicker
              label="Slogan 颜色"
              value={cardContentSettings.sloganColor}
              onChange={(v) => updateCardContentSettings({ sloganColor: v })}
              presets={colorPresets}
              isDarkMode={isDarkMode}
            />
            {/* 特点颜色 */}
            <ColorPicker
              label="特点圆点颜色"
              value={cardContentSettings.featureColor || '#22c55e'}
              onChange={(v) => updateCardContentSettings({ featureColor: v })}
              presets={colorPresets}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>

        {/* 文字位置 */}
        <div>
          <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            文字位置
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>左侧边距</span>
              <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {cardContentSettings.textPaddingLeft}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="20"
              step="1"
              value={cardContentSettings.textPaddingLeft}
              onChange={(e) => updateCardContentSettings({ textPaddingLeft: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
            />
            <div className={`flex justify-between text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <span>左对齐</span>
              <span>居中</span>
              <span>右偏移</span>
            </div>
          </div>
        </div>

        {/* 背景颜色设置 */}
        <div>
          <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            背景颜色
          </h4>
          {/* 主要选项：透明、白色、黑色、灰色 */}
          <div className="flex gap-2 mb-3">
            {bgColorPresets.map((preset) => (
              <button
                key={preset.value}
                onClick={() => {
                  if (preset.value === 'transparent') {
                    toggleCanvasBackground();
                  } else {
                    setCanvasBackgroundColor(preset.value);
                  }
                }}
                className={`relative w-10 h-10 rounded-lg border-2 transition-all overflow-hidden ${
                  (preset.value === 'transparent' && !showCanvasBackground) ||
                  (preset.value !== 'transparent' && showCanvasBackground && canvasBackgroundColor === preset.value)
                    ? 'border-blue-500 ring-2 ring-blue-500/30'
                    : isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                }`}
                title={preset.name}
              >
                {preset.icon === 'checkerboard' ? (
                  /* 棋盘格透明背景 */
                  <div className="w-full h-full" style={{
                    backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                    backgroundSize: '8px 8px',
                    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                  }} />
                ) : (
                  <div className="w-full h-full" style={{ backgroundColor: preset.value }} />
                )}
              </button>
            ))}
          </div>

          {/* 更多颜色展开/收起 */}
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className={`w-full flex items-center justify-center gap-1.5 py-1.5 text-xs rounded-lg transition-colors ${
              showColorPicker
                ? 'bg-blue-500/10 text-blue-600'
                : isDarkMode
                ? 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            <Palette className="w-3 h-3" />
            {showColorPicker ? '收起颜色' : '更多颜色'}
          </button>

          {/* 颜色网格 */}
          {showColorPicker && (
            <div className="mt-3 space-y-3">
              {/* 预设颜色网格 */}
              <div className="grid grid-cols-6 gap-1.5">
                {moreBgColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setCanvasBackgroundColor(color)}
                    className={`w-full aspect-square rounded-md border-2 transition-all ${
                      showCanvasBackground && canvasBackgroundColor === color
                        ? 'border-blue-500 scale-110 z-10'
                        : isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>

              {/* 自定义颜色输入 */}
              <div className="flex gap-2">
                <input
                  type="color"
                  value={canvasBackgroundColor || '#ffffff'}
                  onChange={(e) => setCanvasBackgroundColor(e.target.value)}
                  className="w-10 h-8 rounded border-0 cursor-pointer"
                />
                <input
                  type="text"
                  value={canvasBackgroundColor || '#ffffff'}
                  onChange={(e) => {
                    if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                      setCanvasBackgroundColor(e.target.value);
                    }
                  }}
                  placeholder="#ffffff"
                  className={`flex-1 px-2 py-1 text-xs rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-200'
                      : 'bg-white border-gray-200 text-gray-700'
                  }`}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 开关项组件
function ToggleItem({
  icon,
  label,
  checked,
  onChange,
  isDarkMode,
}: {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  isDarkMode: boolean;
}) {
  return (
    <div 
      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
      }`}
      onClick={() => onChange(!checked)}
    >
      <div className="flex items-center gap-2">
        <span className={checked ? 'text-blue-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
          {icon}
        </span>
        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          {label}
        </span>
      </div>
      <div className={`w-8 h-4 rounded-full transition-colors ${
        checked ? 'bg-blue-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
      }`}>
        <div className={`w-3 h-3 rounded-full bg-white shadow transition-transform mt-0.5 ${
          checked ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'
        }`} />
      </div>
    </div>
  );
}

// 颜色选择器组件
function ColorPicker({
  label,
  value,
  onChange,
  presets,
  isDarkMode,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  presets: { name: string; value: string }[];
  isDarkMode: boolean;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
        <div 
          className="w-4 h-4 rounded border border-gray-300" 
          style={{ backgroundColor: value || (isDarkMode ? '#9ca3af' : '#6b7280') }} 
        />
      </div>
      <div className="flex flex-wrap gap-1">
        {presets.map((preset) => (
          <button
            key={preset.name}
            onClick={() => onChange(preset.value)}
            className={`w-6 h-6 rounded border-2 transition-all ${
              value === preset.value 
                ? 'border-blue-500 scale-110' 
                : isDarkMode ? 'border-gray-600' : 'border-gray-200'
            }`}
            style={{ 
              backgroundColor: preset.value || (isDarkMode ? '#374151' : '#f3f4f6'),
            }}
            title={preset.name}
          />
        ))}
      </div>
    </div>
  );
}

export default CardStylePanel;
